import { z } from 'zod';
import { MEETING_EVENT_TYPE_VALUES, ONLINE_PLATFORM_VALUES } from '@fas/types';

// ── Meeting platforms (Phase 3I, ADR-064) ──────────────────────────────────────
export const MEETING_MAX_EVENTS = 500;

/** A meeting's identifier as its platform shows it (a Zoom meeting number, a Meet code, a Teams id). */
const meetingIdSchema = z.string('Enter the meeting’s id').trim().min(1, 'Enter the meeting’s id').max(200);

/** One join or leave, as an integration relays it from a meeting platform. */
export const meetingEventSchema = z
  .object({
    platform: z.enum(ONLINE_PLATFORM_VALUES),
    /** The event's id at the sender: the same event sent again is recorded once. */
    eventId: z.string().trim().min(1).max(200),
    type: z.enum(MEETING_EVENT_TYPE_VALUES),
    meetingId: meetingIdSchema,
    /** With an offset. */
    occurredAt: z.iso.datetime({ offset: true }),
    /** Who joined or left: the faculty member's e-mail or employee number (others are not kept). */
    participant: z
      .object({
        email: z.email().max(254).optional(),
        employeeNumber: z.string().trim().min(1).max(30).optional(),
      })
      .strict()
      .refine((p) => !!p.email || !!p.employeeNumber, 'Give the participant’s e-mail or employee number'),
    /** One participation (one device's join and its leave), when the platform names it. */
    participationId: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type MeetingEventInput = z.infer<typeof meetingEventSchema>;

export const meetingEventBatchSchema = z
  .object({ events: z.array(meetingEventSchema).min(1).max(MEETING_MAX_EVENTS) })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.events.forEach((e, i) => {
      if (seen.has(e.eventId)) ctx.addIssue({ code: 'custom', path: ['events', i, 'eventId'], message: 'This event appears twice in the batch' });
      seen.add(e.eventId);
    });
  });
export type MeetingEventBatchInput = z.infer<typeof meetingEventBatchSchema>;

/**
 * A Zoom webhook notification (only what is used; Zoom adds fields over time). The endpoint
 * validation request carries `payload.plainToken`; participant events carry the meeting and the
 * participant with `join_time` or `leave_time`.
 */
export const zoomWebhookSchema = z.object({
  event: z.string().min(1).max(100),
  event_ts: z.number().optional(),
  payload: z
    .object({
      plainToken: z.string().min(1).max(200).optional(),
      account_id: z.string().max(100).optional(),
      object: z
        .object({
          id: z.union([z.string().max(100), z.number()]).optional(),
          uuid: z.string().max(200).optional(),
          participant: z
            .object({
              email: z.string().max(254).optional(),
              id: z.string().max(200).optional(),
              participant_uuid: z.string().max(200).optional(),
              user_id: z.string().max(200).optional(),
              join_time: z.string().max(40).optional(),
              leave_time: z.string().max(40).optional(),
            })
            .optional(),
        })
        .optional(),
    })
    .optional(),
});
export type ZoomWebhookInput = z.infer<typeof zoomWebhookSchema>;
